/**
 * Users API module
 * Wraps baobab-api /users routes.
 *
 * Covers: account info, profile management, and offline downloads.
 */

import client from './client'
import type { ApiResponse } from './client'
import type { Subscription } from './subscriptions'

// ─── Types ────────────────────────────────────────────────────────────────────

export type SubscriptionTier = 'free' | 'basic' | 'standard' | 'premium'

export interface Profile {
  id: string
  display_name: string
  avatar_url: string | null
  bio: string | null
  date_of_birth: string | null
  preferred_genres: string[]
  is_kids_profile: boolean
  created_at: string
  updated_at: string
}

export interface UserMe {
  id: string
  phone: string | null
  email: string | null
  country_code: string | null
  preferred_language: string
  /** Multi-select language preferences (migration 008). Authoritative source for the settings UI. */
  preferred_languages: string[]
  subscription_tier: SubscriptionTier
  profiles: Profile[]
  subscription: Subscription | null
  first_name: string | null
  last_name: string | null
}

export interface UpdateMePayload {
  email?: string
  preferred_language?: string
  /** Multi-select: replaces preferred_languages array in full. */
  preferred_languages?: string[]
  country_code?: string
  first_name?: string
  last_name?: string
}

// ─── Payment History ──────────────────────────────────────────────────────────
// Shape mirrors the SELECT in GET /api/v1/payments/history exactly.
//
// There is NO `created_at` column on payments — the timestamps are
// `initiated_at` (always set) and `completed_at` (null until settled).
// Reading `created_at` here is what produced "Invalid Date" in the UI.

export interface PaymentHistoryItem {
  id: string
  amount: number
  currency: string
  operator: string
  operator_transaction_id: string | null
  status: 'completed' | 'pending' | 'failed'
  initiated_at: string
  completed_at: string | null
}

export async function getPaymentHistory() {
  const { data } = await client.get<ApiResponse<PaymentHistoryItem[]>>('/payments/history')
  return data
}

export interface CreateProfilePayload {
  display_name: string
  avatar_url?: string
  bio?: string
  date_of_birth?: string
  preferred_genres?: string[]
  is_kids_profile?: boolean
}

export interface UpdateProfilePayload {
  display_name?: string
  avatar_url?: string
  bio?: string
  date_of_birth?: string
  preferred_genres?: string[]
  is_kids_profile?: boolean
}

export type DownloadQuality = '360p' | '480p' | '720p'

export interface Download {
  id: string
  content_id: string
  episode_id: string | null
  quality: DownloadQuality
  device_id: string
  file_size_bytes: number | null
  downloaded_at: string
  expires_at: string
  // Joined content metadata
  title: string
  thumbnail_url: string | null
  content_type: string
  episode_title: string | null
  season_number: number | null
  episode_number: number | null
}

export interface AddDownloadPayload {
  content_id: string
  episode_id?: string
  device_id: string
  quality?: DownloadQuality
  file_size_bytes?: number
}

// ─── Account ──────────────────────────────────────────────────────────────────

/** Bootstrap call — returns user + all profiles + active subscription in one shot. */
export async function getMe() {
  const { data } = await client.get<ApiResponse<UserMe>>('/users/me')
  return data
}

export async function updateMe(payload: UpdateMePayload) {
  const { data } = await client.patch<
    ApiResponse<Pick<UserMe, 'id' | 'phone' | 'email' | 'country_code' | 'preferred_language' | 'subscription_tier'>>
  >('/users/me', payload)
  return data
}

// ─── Profiles ─────────────────────────────────────────────────────────────────

export async function getProfiles() {
  const { data } = await client.get<ApiResponse<Profile[]>>('/users/me/profiles')
  return data
}

export async function createProfile(payload: CreateProfilePayload) {
  const { data } = await client.post<ApiResponse<Profile>>('/users/me/profiles', payload)
  return data
}

export async function updateProfile(profile_id: string, payload: UpdateProfilePayload) {
  const { data } = await client.patch<ApiResponse<Profile>>(
    `/users/me/profiles/${profile_id}`,
    payload
  )
  return data
}

export async function deleteProfile(profile_id: string) {
  await client.delete(`/users/me/profiles/${profile_id}`)
}

// ─── Downloads ────────────────────────────────────────────────────────────────

/** Returns active (non-expired) downloads with content metadata. Requires standard+ tier. */
export async function getDownloads() {
  const { data } = await client.get<ApiResponse<Download[]>>('/users/me/downloads')
  return data
}

export async function addDownload(payload: AddDownloadPayload) {
  const { data } = await client.post<ApiResponse<Download>>('/users/me/downloads', payload)
  return data
}

export async function deleteDownload(download_id: string) {
  await client.delete(`/users/me/downloads/${download_id}`)
}
